import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import {
  ContextItem,
  RequirementAction,
  RequirementAdditionalData,
  RequirementContextType,
  RequirementStatusViewProps,
  RequirementTableCallback
} from "../types";
import { useMarkRequirementAsCompleteModal } from "../components/MarkRequirementAsCompleteModal";
import { useMarkRequirementAsIncompleteModal } from "../components/MarkRequirementAsIncompleteModal";
import { useReRequestRequirementModal } from "../components/ReRequestRequirementModal";
import { RequirementStatus } from "@wingspanhq/payments/dist/interfaces/requirement";
import { useRevertRequirementModal } from "../components/RevertRequirementModal";
import { ALL_REQUIREMENT_EVENTS, getIsGeneralRequirement } from "../utils";
import { selectorIsRequirementPostApproval } from "../selectors/selectorIsRequirementPostApproval";
import { useExtendExpirationDateModal } from "../components/ExtendExpirationDateModal/useExtendExpirationDateModal";
import { useRenewRequirementAsPayerModal } from "../components/RenewRequirementModal/useRenewRequirementAsPayerModal";
import { useRenewRequirementAsPayeeModal } from "../components/RenewRequirementModal/useRenewRequirementAsPayeeModal";
import { selectorIsRequirementArchived } from "../selectors/selectorIsRequirementArchived";

const hideGroupLabelIfEmpty = (group: RequirementAction[]) => {
  return group.filter(action =>
    action.groupLabel
      ? group.some(action => !action.groupLabel && action.visible !== false)
      : true
  );
};

export const useRequirementDefaultActions = (
  contextType: RequirementContextType,
  callback: RequirementTableCallback
) => {
  const { openSnackbar } = useWSSnackbar();
  const markRequirementAsCompleteModal = useMarkRequirementAsCompleteModal();
  const markRequirementAsIncompleteModal =
    useMarkRequirementAsIncompleteModal();
  const reRequestRequirementModal = useReRequestRequirementModal();
  const revertRequirementModal = useRevertRequirementModal();
  const extendExpirationDateModal = useExtendExpirationDateModal();
  const renewRequirementAsPayerModal = useRenewRequirementAsPayerModal();
  const renewRequirementAsPayeeModal = useRenewRequirementAsPayeeModal();

  const handleRenewRequirementAsPayer =
    (
      requirement: IRequirementResponse,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      renewRequirementAsPayerModal.open({
        requirement,
        currentStatus,
        onSuccess(requirement) {
          callback(ALL_REQUIREMENT_EVENTS.Renew, requirement);
          openSnackbar({
            message: "Requirement renewed successfully",
            duration: 3000,
            type: "success"
          });
        }
      });
    };

  const handleRenewRequirementAsPayee =
    (
      requirement: IRequirementResponse,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      renewRequirementAsPayeeModal.open({
        requirement,
        currentStatus,
        onSuccess(requirement) {
          callback(ALL_REQUIREMENT_EVENTS.Renew, requirement);
          openSnackbar({
            message: "Requirement renewed successfully",
            duration: 3000,
            type: "success"
          });
        }
      });
    };

  const handleExtendExpirationDate =
    (
      requirement: IRequirementResponse,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      extendExpirationDateModal.open({
        requirement,
        onSuccess(requirement) {
          callback(ALL_REQUIREMENT_EVENTS.ExtendExpirationDate, requirement);
          openSnackbar({
            message: "Requirement expiration updated",
            duration: 3000,
            type: "success"
          });
        }
      });
    };

  const handleRevertRequirementStatus =
    (
      requirement: IRequirementResponse,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      revertRequirementModal.open({
        requirement,
        context,
        additionalData,
        onSuccess() {
          callback(ALL_REQUIREMENT_EVENTS.ResetStatus, requirement);
          openSnackbar({
            message: "Requirement status reverted successfully",
            duration: 3000,
            type: "success"
          });
        },
        currentStatus
      });
    };

  const handleMarkRequirementAsCompleted =
    (
      requirement: IRequirementResponse,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      markRequirementAsCompleteModal.open({
        requirement,
        context,
        additionalData,
        onSuccess() {
          callback(ALL_REQUIREMENT_EVENTS.OverrideStatus, requirement);
          openSnackbar({
            message: "Requirement marked as complete successfully",
            duration: 3000,
            type: "success"
          });
        },
        currentStatus
      });
    };

  const handleMarkRequirementAsIncomplete =
    (
      requirement: IRequirementResponse,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      markRequirementAsIncompleteModal.open({
        requirement,
        context,
        additionalData,
        onSuccess() {
          callback(ALL_REQUIREMENT_EVENTS.OverrideStatus, requirement);
          openSnackbar({
            message: "Requirement marked as incomplete successfully",
            duration: 3000,
            type: "success"
          });
        },
        currentStatus
      });
    };

  const handleReRequestRequirement =
    (
      requirement: IRequirementResponse,
      context: ContextItem,
      additionalData: RequirementAdditionalData,
      currentStatus: RequirementStatusViewProps
    ) =>
    () => {
      reRequestRequirementModal.open({
        requirement,
        context,
        additionalData,
        onSuccess() {
          callback(ALL_REQUIREMENT_EVENTS.OverrideStatus, requirement);
          openSnackbar({
            message: "Requirement re-requested successfully",
            duration: 3000,
            type: "success"
          });
        },
        currentStatus
      });
    };

  return (
    requirement: IRequirementResponse,
    context: ContextItem,
    additionalDataItem: RequirementAdditionalData,
    currentStatus: RequirementStatusViewProps
  ): {
    extendToolbarActions(actions: RequirementAction[]): RequirementAction[];
  } => {
    let actions: RequirementAction[] = [];
    const isArchivedRequirement = selectorIsRequirementArchived(requirement);

    if ("requirementId" in requirement && isArchivedRequirement) {
      actions = [];
    } else if (
      "requirementId" in requirement &&
      contextType === RequirementContextType.Payee
    ) {
      const reRequestActions: RequirementAction[] = [
        {
          groupLabel: "Reset Requirement"
        },
        {
          label: "Re-request",
          icon: "reset",
          helpText:
            "Resets the status to incomplete and the contractor starts the requirement from scratch.",
          destructive: true,
          onClick: handleReRequestRequirement(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible: !getIsGeneralRequirement(requirement)
        }
      ];

      const extendExpirationActions: RequirementAction[] = [
        {
          groupLabel: "Manage expiration"
        },
        {
          label: "Request renewal",
          buttonIcon: null,
          helpText: requirement.expiresAt
            ? "Manually starts early renewal process prior to automatic reminders which start 30 days before expiration."
            : "Manually allows contractors to upload a new version of their document.",
          icon: "refresh-h",
          onClick: handleRenewRequirementAsPayer(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            !getIsGeneralRequirement(requirement) &&
            requirement.status === RequirementStatus.Complete
        },
        {
          label: "Update expiration date",
          buttonIcon: null,
          helpText: "Change the expiration date to a different date.",
          icon: "calendar",
          onClick: handleExtendExpirationDate(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            !getIsGeneralRequirement(requirement) &&
            requirement.status === RequirementStatus.Complete
        }
      ];

      const isReadyForApprove =
        requirement.status === RequirementStatus.PendingReview;

      const isPostApproval = selectorIsRequirementPostApproval(requirement);

      const defaultActions: RequirementAction[] = [
        {
          groupLabel: "Manage approvals"
        },
        {
          label: "Approve (mark complete)",
          buttonLabel: "Approve",
          buttonKind: isPostApproval ? undefined : "Primary",
          buttonOrder: 2,
          icon: "accept",
          buttonIcon: null,
          helpText:
            "Approves the submission and marks the requirement as complete and overwrites current status.",
          onClick: handleMarkRequirementAsCompleted(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            isReadyForApprove ||
            (isPostApproval &&
              requirement.status !== RequirementStatus.Complete),
          mainAction: true
        },
        {
          label: "Reject (mark incomplete)",
          buttonLabel: "Reject",
          buttonIcon: null,
          buttonKind: isPostApproval ? undefined : "Secondary",
          // destructive: true,
          helpText:
            "Rejects the submission and marks the requirements as incomplete and overwrites current status.",
          icon: "dispute",
          onClick: handleMarkRequirementAsIncomplete(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            isReadyForApprove ||
            (isPostApproval &&
              requirement.status !== RequirementStatus.Incomplete),
          mainAction: true
        },
        {
          label: "Mark complete",
          icon: "accept",
          buttonIcon: null,
          helpText:
            "Overwritten status will be updated for both you and the contractor",
          onClick: handleMarkRequirementAsCompleted(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            !isReadyForApprove &&
            !isPostApproval &&
            requirement.payerOverrideStatus !== RequirementStatus.Complete
        },
        {
          label: "Mark incomplete",
          buttonIcon: null,
          helpText:
            "Overwritten status will be updated for both you and the contractor",
          icon: "dispute",
          onClick: handleMarkRequirementAsIncomplete(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            !isReadyForApprove &&
            !isPostApproval &&
            requirement.payerOverrideStatus !== RequirementStatus.Incomplete
        },
        {
          label: "Revert to synced status",
          buttonIcon: null,
          helpText: "Undo overwritten status and revert to synced status",
          icon: "undo",
          onClick: handleRevertRequirementStatus(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            !isReadyForApprove &&
            !isPostApproval &&
            !!requirement?.payerOverrideStatus
        }
      ];

      actions = [
        ...hideGroupLabelIfEmpty(defaultActions),
        ...hideGroupLabelIfEmpty(extendExpirationActions),
        ...hideGroupLabelIfEmpty(reRequestActions)
      ];
    } else if (
      "requirementId" in requirement &&
      contextType === RequirementContextType.Payer
    ) {
      actions = [
        {
          label: "Start renewal",
          buttonIcon: null,
          icon: "refresh-h",
          onClick: handleRenewRequirementAsPayee(
            requirement,
            context,
            additionalDataItem,
            currentStatus
          ),
          visible:
            !!requirement.manualRenewalAllowed &&
            requirement.status === RequirementStatus.Complete
        }
      ];
    }

    return {
      extendToolbarActions: originalActions => {
        return [...originalActions, ...actions];
      }
    };
  };
};
